import React, { useEffect, useState } from "react";
import { X, ChevronRight, Sparkles } from "lucide-react";
import confetti from "canvas-confetti";

/**
 * CandidateGuidedTour — Tour interactif 3 étapes pour candidat fraîchement validé.
 * Se déclenche automatiquement quand profile_status devient "validated" ou "premium"
 * et qu'il n'a pas encore vu le tour (sessionStorage flag).
 */

const STEPS = [
    {
        title: "🎉 Votre profil est validé !",
        body: "Bienvenue dans l'équipe Renforteo. Vous pouvez maintenant postuler à toutes les missions ouvertes qui correspondent à votre métier. Notre équipe a vérifié votre SIRET, KBIS, identité et domicile — tout est en règle.",
        cta: "Voir les missions →",
        goto: "missions",
    },
    {
        title: "1. Parcourez les missions près de chez vous",
        body: "Utilisez la barre de filtres pour cibler votre métier, vos dates et le degré d'urgence. Passez en vue Carte pour visualiser les missions autour de vous. Distance et rémunération sont affichées sur chaque carte.",
        cta: "OK, j'ai compris",
        goto: null,
    },
    {
        title: "2. Postulez en un clic",
        body: "Cliquez sur « Postuler » sur la mission qui vous intéresse. Le client reçoit votre candidature en temps réel avec votre profil, votre score de compatibilité et vos badges vérifiés.",
        cta: "Continuer",
        goto: null,
    },
    {
        title: "3. Devenez Super Talent 🌟",
        body: "Après 10 missions terminées avec une note ≥ 4,7/5 et zéro no-show, vous obtenez le badge doré Super Talent : visibilité prioritaire auprès des clients, missions urgentes en avant-première.",
        cta: "C'est parti !",
        goto: null,
    },
];

export function fireConfetti() {
    const duration = 2500;
    const end = Date.now() + duration;
    const colors = ["#F5A623", "#0B192C", "#10b981", "#ffffff"];
    (function frame() {
        confetti({
            particleCount: 3,
            angle: 60,
            spread: 70,
            origin: { x: 0, y: 0.7 },
            colors,
        });
        confetti({
            particleCount: 3,
            angle: 120,
            spread: 70,
            origin: { x: 1, y: 0.7 },
            colors,
        });
        if (Date.now() < end) requestAnimationFrame(frame);
    })();
}

export default function CandidateGuidedTour({ open, onClose, onGoto }) {
    const [i, setI] = useState(0);
    useEffect(() => {
        if (open) {
            setI(0);
            // Small confetti burst on tour open
            confetti({
                particleCount: 80,
                spread: 90,
                origin: { y: 0.6 },
                colors: ["#F5A623", "#0B192C", "#10b981"],
            });
        }
    }, [open]);

    if (!open) return null;
    const step = STEPS[i];
    const last = i === STEPS.length - 1;

    const next = () => {
        if (last) {
            close();
            if (step.goto) onGoto && onGoto(step.goto);
        } else {
            if (i === 0 && step.goto) onGoto && onGoto(step.goto);
            setI(i + 1);
        }
    };

    const close = () => {
        try {
            sessionStorage.setItem("renforteo_tour_seen", "1");
        } catch {}
        onClose && onClose();
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="candidate-guided-tour"
        >
            <div
                className="relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
                style={{
                    background: "linear-gradient(135deg, #0B192C 0%, #142845 60%, #1a3a63 100%)",
                    animation: "tourEnter 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
                }}
            >
                <button
                    onClick={close}
                    className="absolute top-3 right-3 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors"
                    data-testid="tour-close"
                    aria-label="Fermer"
                >
                    <X size={16} />
                </button>

                {/* Sparkle top-left */}
                <div className="absolute top-4 left-4 text-[#F5A623]">
                    <Sparkles size={22} className="animate-pulse" />
                </div>

                <div className="pt-14 pb-6 px-8 text-white text-center">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#F5A623] mb-3">
                        Étape {i + 1} sur {STEPS.length}
                    </div>
                    <h2
                        className="font-display text-2xl md:text-3xl font-bold mb-4 leading-tight"
                        key={`t-${i}`}
                        style={{ animation: "tourStepFade 0.5s ease-out" }}
                    >
                        {step.title}
                    </h2>
                    <p
                        className="text-sm md:text-base text-white/85 leading-relaxed mb-6"
                        key={`b-${i}`}
                        style={{ animation: "tourStepFade 0.5s ease-out 0.1s both" }}
                    >
                        {step.body}
                    </p>

                    {/* Progress dots */}
                    <div className="flex justify-center gap-1.5 mb-6">
                        {STEPS.map((_, idx) => (
                            <div
                                key={idx}
                                className={`h-1.5 rounded-full transition-all ${
                                    idx === i ? "w-8 bg-[#F5A623]" :
                                    idx < i ? "w-1.5 bg-white/70" : "w-1.5 bg-white/25"
                                }`}
                            />
                        ))}
                    </div>

                    <button
                        onClick={next}
                        className="w-full py-3 rounded-xl bg-[#F5A623] text-[#0B192C] font-bold text-sm hover:scale-[1.02] transition-transform shadow-lg inline-flex items-center justify-center gap-2"
                        data-testid="tour-next"
                    >
                        {step.cta}
                        {!last && <ChevronRight size={16} />}
                    </button>

                    {!last && (
                        <button
                            onClick={close}
                            className="mt-3 text-white/50 hover:text-white/80 text-xs transition-colors"
                            data-testid="tour-skip"
                        >
                            Passer le tour
                        </button>
                    )}
                </div>

                <style>{`
                    @keyframes tourEnter {
                        from { opacity: 0; transform: scale(0.9); }
                        to { opacity: 1; transform: scale(1); }
                    }
                    @keyframes tourStepFade {
                        from { opacity: 0; transform: translateY(8px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
            </div>
        </div>
    );
}
